/**
 * The one set of formats (v7 Part D §7; CORE-1 §5). Every screen shows dates, quantities and money through these.
 *
 *   date('2026-10-02')          → 02 Oct 2026          dateShort → 02 Oct        dateTime → 02 Oct 2026, 18:30
 *   time('…T13:00:00Z')         → 18:30 (24-hour, IST)
 *   kg(36050)  (grams)          → 36.1 kg             units(12) → 12
 *   rupees(12345000) (paise)    → ₹1,23,450            rupees(12345050, { paise: true }) → ₹1,23,450.50
 *   num(1234567)                → 12,34,567 (Indian grouping)
 *
 * A null shows as an em dash: an unknown is never printed as 0.
 */
const IST = 'Asia/Kolkata';
const NONE = '—';
const asDate = (v: string | number | Date): Date => {
  if (v instanceof Date) return v;
  // A plain date (YYYY-MM-DD) is a business day, not a moment: read it at noon UTC so no zone moves it.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(`${v}T12:00:00Z`);
  return new Date(v);
};
const valid = (d: Date) => !Number.isNaN(d.getTime());
const fmtDay = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: IST });
const fmtDayShort = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', timeZone: IST });
const fmtTime = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: IST });
const indian = new Intl.NumberFormat('en-IN');

export function date(v: string | number | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return NONE;
  const d = asDate(v); return valid(d) ? fmtDay.format(d) : NONE;
}
export function dateShort(v: string | number | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return NONE;
  const d = asDate(v); return valid(d) ? fmtDayShort.format(d) : NONE;
}
export function time(v: string | number | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return NONE;
  const d = asDate(v); return valid(d) ? fmtTime.format(d) : NONE;
}
export function dateTime(v: string | number | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return NONE;
  const d = asDate(v); return valid(d) ? `${fmtDay.format(d)}, ${fmtTime.format(d)}` : NONE;
}
export function num(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return NONE;
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
}
/** Grams → kilograms to one decimal (milk on screens; the data holds grams). */
export function kg(grams: number | null | undefined): string {
  if (grams === null || grams === undefined || Number.isNaN(grams)) return NONE;
  return `${num(grams / 1000, 1)} kg`;
}
/** Kilograms already (a report column in kg) to one decimal. */
export function kgValue(kilograms: number | null | undefined): string {
  if (kilograms === null || kilograms === undefined || Number.isNaN(kilograms)) return NONE;
  return `${num(kilograms, 1)} kg`;
}
export function units(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return NONE;
  return indian.format(Math.round(v));
}
/** Paise → rupees with Indian grouping; whole rupees on screens, paise only when asked (reports). */
export function rupees(paise: number | null | undefined, opts: { paise?: boolean } = {}): string {
  if (paise === null || paise === undefined || Number.isNaN(paise)) return NONE;
  const r = paise / 100;
  const sign = r < 0 ? '−' : '';
  return `${sign}₹${num(Math.abs(r), opts.paise ? 2 : 0)}`;
}
export function percent(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return NONE;
  return `${num(v, digits)} %`;
}
